'use client';

import { useEffect, useRef, useState } from 'react';
import { faLocationDot } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { SuggestionSection } from './suggestion-section.component';
import { CityBranchCard } from './city-branch-card.component';

import { Icon } from '@/components/ui/icon.component';
import { Link } from '@/components/ui/link/link.component';
import {
  CityBranches,
  hasRenderableCityBranchSuggestions,
} from '@/app/(main)/(pages)/busca/[slug]/_services/get-city-branches.service';
import { getCitySlug } from '@/app/(main)/(pages)/_services/get-city-slug.service';
import { branchesListId } from '@/app/(main)/(pages)/_components/branches-section/consts/branches-section.const';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { useUserLocation } from '@/hooks/use-user-location.hook';

interface CityBranchesSuggestionProps {
  cityBranches: CityBranches | null;
}

export function CityBranchesSuggestion({ cityBranches }: CityBranchesSuggestionProps) {
  const analytics = getAnalyticsPlugin();
  const hasTrackedClickRef = useRef(false);
  const { userLocation } = useUserLocation();
  const [citySlug, setCitySlug] = useState<string | null>(null);

  const canRender = hasRenderableCityBranchSuggestions(cityBranches);

  useEffect(() => {
    if (!canRender || !userLocation?.city || !userLocation?.state) {
      return;
    }

    getCitySlug(userLocation.city, userLocation.state).then((city) => {
      setCitySlug(city?.slug ?? null);
    });
  }, [canRender, userLocation?.city, userLocation?.state]);

  if (!canRender) {
    return null;
  }

  const [featured, preview] = cityBranches.branches;

  const trackFeaturedClick = () => {
    if (hasTrackedClickRef.current) {
      return;
    }

    hasTrackedClickRef.current = true;
    analytics.track(AnalyticsEnum.SEARCH_SUGGESTION_CITY_BRANCH_SELECTED, {
      partner_id: featured.id,
      distance_km: featured.distance ?? undefined,
    });
  };

  const trackSeeAll = () => {
    analytics.track(AnalyticsEnum.SEARCH_SUGGESTION_CITY_BRANCH_SEE_ALL, {
      city: userLocation?.city,
      qtd_unidades: cityBranches.count,
    });
  };

  const allBranchesUrl = citySlug ? `/cidades/${citySlug}#${branchesListId}` : '/unidades-perto-de-voce';

  return (
    <SuggestionSection
      icon={<Icon icon={faLocationDot} className="text-xl text-green-900" />}
      title="Unidades físicas na sua cidade"
      featuredCard={(
        <div onClickCapture={trackFeaturedClick}>
          <CityBranchCard branch={featured} citySlug={citySlug} />
        </div>
      )}
      previewCard={preview && <CityBranchCard branch={preview} citySlug={citySlug} isPreview />}
      cta={(
        <Link
          href={allBranchesUrl}
          onClick={trackSeeAll}
          data-testid={DataTestIdEnum.SEARCH_PAGE_SUGGESTION_CITY_BRANCH_CTA}
          className="flex w-full items-center justify-center rounded-lg bg-success-300 px-4 py-3 text-lg font-semibold text-success-800 transition-colors hover:bg-success-400 hover:text-success-900 active:bg-success-500"
        >
          Ver mais unidades
        </Link>
      )}
    />
  );
}
